import { createInvoice } from "@/api/broker/createInvoice";
import { Load } from "@/api/broker/getLoadsToBeInvoiced";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns/format";
import { useState } from "react";

interface Props {
  loads: Load[];
  setDialogOpen: (open: boolean) => void;
}

export function BrokerInvoiceDialogContent({ loads, setDialogOpen }: Props) {
  const [selectedLoads, setSelectedLoads] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const handleLoadSelection = (loadId: string, checked: boolean) => {
    if (checked) {
      setSelectedLoads([...selectedLoads, loadId]);
    } else {
      setSelectedLoads(selectedLoads.filter((id) => id !== loadId));
    }
  };

  const handleCreateInvoice = async () => {
    if (selectedLoads.length === 0) return;

    const selectedLoadData = loads?.filter((load) =>
      selectedLoads.includes(load.id)
    );

    const newInvoice = {
      loadIds: selectedLoads,
    };

    await createInvoice(newInvoice);
    setSelectedLoads([]);

    queryClient.invalidateQueries({
      queryKey: ["broker", "invoices"],
    });

    setDialogOpen(false);
  };

  const [invoiceNotes, setInvoiceNotes] = useState("");
  return (
    <DialogContent className="overflow-y-auto min-w-[800px] min-h-[800px]">
      <DialogHeader>
        <DialogTitle>Create New Invoice</DialogTitle>
        <DialogDescription>
          Select loads to include in this invoice
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 m-5">
        <div className="grid grid-cols-2 ">
          <div>
            <Label htmlFor="customer">Customer</Label>
            <Select value={"Receiver"}>
              <SelectTrigger>
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={"VS"} value={"Receiver"}>
                  {"Receiver"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Notes (Optional)</Label>
          <Textarea
            id="notes"
            placeholder="Add any additional notes for this invoice..."
            value={invoiceNotes}
            onChange={(e) => setInvoiceNotes(e.target.value)}
          />
        </div>

        <div>
          <Label>Available Loads</Label>
          <div className="border rounded-lg max-h-120 overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Load Name</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loads.length > 0 ? (
                  loads.map((load) => (
                    <TableRow key={load.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedLoads.includes(load.id)}
                          onCheckedChange={(checked) =>
                            handleLoadSelection(load.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">{load.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{load.pickUpLocation}</div>
                          <div className="text-muted-foreground">
                            → {load.deliveryLocation}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {load.pickUpStart
                          ? format(load.pickUpStart, "MM-dd-yy")
                          : "--"}
                      </TableCell>
                      <TableCell>
                        $
                        {load.amountCents != null
                          ? (load.amountCents / 100).toFixed(2)
                          : "0.00"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      No available loads.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateInvoice}
            disabled={selectedLoads.length === 0}
          >
            Create Invoice
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
